import React, { useEffect, useRef, useState } from 'react';
import 'pannellum/build/pannellum.css';

const PanoramaViewer = ({ image, title }) => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !image) return;

    let mounted = true;
    let scriptLoaded = false;
    
    // Reset states when image changes
    setIsLoading(true);
    setLoadError(false);
    
    // Function to initialize the viewer
    const initViewer = () => {
      try {
        if (!mounted || !containerRef.current) return;

        // Destroy previous viewer completely
        if (viewerRef.current) {
          try {
            viewerRef.current.destroy();
            viewerRef.current = null;
          } catch (e) {
            console.log('Previous viewer cleanup:', e);
          }
        }

        // Clear container
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        console.log('🔄 Initializing 360° viewer for:', title);

        // Check if pannellum is available globally
        if (!window.pannellum || !window.pannellum.viewer) {
          console.error('❌ Pannellum viewer not available on window');
          if (mounted) {
            setLoadError(true);
            setIsLoading(false);
          }
          return;
        }

        // Create the viewer with proper configuration
        viewerRef.current = window.pannellum.viewer(containerRef.current, {
            type: 'equirectangular',
            panorama: image,
            autoLoad: true,
            
            // View settings
            pitch: 0,
            yaw: 180,
            hfov: 100,
            minHfov: 50,
            maxHfov: 120,
            
            // Controls
            showControls: true,
            showZoomCtrl: true,
            showFullscreenCtrl: true,
            
            // Interaction
            mouseZoom: true,
            draggable: true,
            keyboardZoom: true,
            doubleClickZoom: true,
            
            // Auto rotation
            autoRotate: -2,
            autoRotateInactivityDelay: 3000,
            autoRotateStopDelay: 3000,
            
            // Other settings
            compass: false,
            hotSpotDebug: false,
            
            // Callbacks
            onLoad: () => {
              if (mounted) {
                console.log('✅ 360° panorama loaded:', title);
                setIsLoading(false);
                setLoadError(false);
              }
            },
            
            onError: (err) => {
              console.error('❌ Panorama error:', err);
              if (mounted) {
                setIsLoading(false);
                setLoadError(true);
              }
            }
          });
      } catch (error) {
        console.error('❌ Error initializing viewer:', error);
        if (mounted) {
          setIsLoading(false);
          setLoadError(true);
        }
      }
    };

    // Load pannellum script if not already loaded
    if (!window.pannellum && !scriptLoaded) {
      const script = document.createElement('script');
      script.src = '/pannellum.js';
      script.async = false;
      script.onload = () => {
        console.log('✅ Pannellum script loaded');
        scriptLoaded = true;
        setTimeout(initViewer, 300);
      };
      script.onerror = () => {
        console.error('❌ Failed to load pannellum script');
        setLoadError(true);
        setIsLoading(false);
      };
      document.head.appendChild(script);
    } else {
      setTimeout(initViewer, 200);
    }

    // Cleanup
    return () => {
      mounted = false;
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch (e) {
          console.log('Cleanup');
        }
      }
    };
  }, [image, title]);

  if (loadError) {
    return (
      <div style={{ 
        width: '100%', 
        height: '100%',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#1a1a1a'
      }}>
        <img 
          src={image} 
          alt={title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '100%',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        backgroundColor: '#000',
        position: 'relative'
      }}
    >
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          fontSize: '14px',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '10px' }}>Loading 360° Panorama...</div>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
    </div>
  );
};

export default PanoramaViewer;
